import React from 'react';
import { Card } from '@/components/ui/card';
import { Package, RefreshCw, Clock, Shield, Thermometer, Mail, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import { LiveArrivalBadge, ShippingCountdown } from '@/components/ShippingFeatures';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen py-16" data-testid="shipping-policy-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
        <h1 className="heading-font text-5xl md:text-6xl font-bold mb-8 text-center">Shipping & Return Policy</h1>
        
        {/* Live Arrival Guarantee Hero */}
        <div className="mb-12">
          <Card className="p-8 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-green-200">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-10 h-10 text-green-600" />
              </div>
              <div className="text-center md:text-left">
                <h2 className="heading-font text-3xl font-bold text-green-800 mb-2">100% Live Arrival Guarantee</h2>
                <p className="text-green-700 text-lg">
                  Every plant ships with our promise: they arrive alive and healthy, or we replace them—no questions asked.
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-8">
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-8 h-8 text-primary" />
              <h2 className="heading-font text-3xl font-semibold">Shipping Policy</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              We ship live freshwater aquarium plants from Springfield, Missouri using <strong>USPS Priority Mail</strong> to ensure fast, reliable delivery across the U.S.
            </p>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Shipping Method:</p>
                  <p className="text-muted-foreground">USPS Priority Mail (1-3 business days)</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Shipping Cost:</p>
                  <p className="text-muted-foreground">$9.99 flat rate anywhere in the United States</p>
                  <p className="text-green-600 font-medium">FREE shipping on orders $60+</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Shipping Days:</p>
                  <p className="text-muted-foreground">Monday through Wednesday</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Daily Cutoff Time:</p>
                  <p className="text-muted-foreground">All orders ship at <strong>6:00 PM Central Time</strong></p>
                  <p className="text-sm text-muted-foreground">Orders received after 6 PM CST will be shipped on the next shipping day</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Local Pickup:</p>
                  <p className="text-muted-foreground">FREE pickup available in Springfield, MO</p>
                  <p className="text-sm text-muted-foreground">Schedule your pickup time at checkout</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-700">Live Arrival Guarantee:</p>
                  <p className="text-muted-foreground">We guarantee live delivery. If plants arrive damaged, we offer replacements or store credit—customer pays $9.99 shipping for replacements.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Weather Holds:</p>
                  <p className="text-muted-foreground">Orders may be delayed during extreme heat or cold to protect plant health</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Tracking:</p>
                  <p className="text-muted-foreground">Every order includes USPS tracking information</p>
                </div>
              </div>
            </div>
            
            {/* Shipping Schedule Visual */}
            <div className="mt-8 p-6 bg-secondary/30 rounded-xl">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Weekly Shipping Schedule
              </h3>
              <div className="grid grid-cols-7 gap-2 text-center text-sm">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                  <div key={day} className={`p-3 rounded-lg ${i >= 1 && i <= 3 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'}`}>
                    <p className="font-semibold">{day}</p>
                    {i >= 1 && i <= 3 ? (
                      <p className="text-xs mt-1">6 PM</p>
                    ) : (
                      <p className="text-xs mt-1">—</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Green = Shipping days | Orders placed after 6 PM ship next business day
              </p>
            </div>
          </Card>

          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <RefreshCw className="w-8 h-8 text-primary" />
              <h2 className="heading-font text-3xl font-semibold">Replacement Policy</h2>
            </div>
            
            {/* Friendly intro message */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
              <p className="text-green-800">
                <strong>A note from our team:</strong> As a small, family-run business, we've implemented these policies to keep our prices competitive while continuing to offer the healthiest, highest-quality plants and animals. We truly appreciate your understanding and support—it's customers like you who help us grow! 🌱
              </p>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Due to the perishable nature of live aquarium plants, <strong>all sales are final</strong>. We do not accept returns or issue monetary refunds. However, we stand behind the quality of our products and offer <strong>replacements or store credit</strong> for eligible claims.
            </p>
            
            {/* Policy Highlight Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Policy Summary
              </h3>
              <ul className="text-sm text-amber-900 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span><strong>No Returns:</strong> We cannot accept returns on live plants or animals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span><strong>No Cash Refunds:</strong> We do not issue monetary refunds</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span><strong>Replacements or Store Credit:</strong> Eligible claims receive replacement products or store credit</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span><strong>Replacement Shipping:</strong> Customer pays $9.99 flat-rate shipping for replacement orders</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Claim Window</h3>
                </div>
                <p className="text-muted-foreground">All claims must be submitted <strong>within 12 hours of delivery</strong>. Claims submitted after this window cannot be honored.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-lg">Live Arrival Guarantee</h3>
                </div>
                <p className="text-muted-foreground">If your plants arrive dead or severely damaged, contact us within 12 hours of delivery with clear photos of the packaging and plants. We will send replacement plants or issue store credit for any verified DOA items.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Replacement Shipping</h3>
                </div>
                <p className="text-muted-foreground">If your claim is approved and replacement plants need to be shipped, <strong>the customer is responsible for the $9.99 flat-rate shipping fee</strong>. Alternatively, replacements can be picked up for free at our Springfield, MO location.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Required Documentation</h3>
                </div>
                <p className="text-muted-foreground">To process your replacement claim, we require:</p>
                <ul className="text-muted-foreground text-sm mt-2 space-y-1 ml-4">
                  <li>• Clear photos of the damaged/dead plants</li>
                  <li>• Photos of the original packaging</li>
                  <li>• Your order number</li>
                  <li>• Brief description of the issue</li>
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Weather-Related Exceptions</h3>
                </div>
                <p className="text-muted-foreground">If your order was shipped during extreme weather conditions against our recommendation, or if you requested shipment despite a weather hold advisory, we may not be able to honor replacement claims. Please monitor weather conditions in your area when placing orders.</p>
              </div>
            </div>
          </Card>

          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-8 h-8 text-primary" />
              <h2 className="heading-font text-3xl font-semibold">How to Contact Us</h2>
            </div>
            <p className="text-muted-foreground mb-4">
              If you have questions about your order, shipping, or plant health, reach out anytime:
            </p>
            <div className="space-y-3">
              <p><strong>Email:</strong> contact.us@aquaplantsplus.com</p>
              <p><strong>Response Time:</strong> We typically reply within 24 hours, Monday–Friday</p>
              <p><strong>Photos for Claims:</strong> Please include clear photos of packaging and plants if reporting damage or DOA</p>
            </div>
          </Card>

          {/* Quick Reference Card */}
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-green-50">
            <h3 className="font-semibold text-xl mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Quick Reference
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">✓ What We Offer</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Live arrival guarantee</li>
                  <li>• Replacement plants for DOA</li>
                  <li>• Store credit option</li>
                  <li>• USPS tracking on every order</li>
                  <li>• Weather-conscious shipping</li>
                  <li>• Free local pickup option</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-amber-700 mb-2">⚠ Policy Reminders</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• No returns accepted</li>
                  <li>• Replacements or store credit (no cash refunds)</li>
                  <li>• $9.99 shipping for replacements</li>
                  <li>• 12-hour claim window</li>
                  <li>• Photos required for all claims</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="text-center text-sm text-muted-foreground">
            <p>Last Updated: January 3, 2026</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
